import Analytics from './Analytics';

/**
 * The WLP Mixpanel V1 super properties (PRD "WLP Program Analytics — Mixpanel V1",
 * Part A §5.1), built from the dashboard's tenant context. The backend's tracker
 * (`Domain/Analytics/Enums/AnalyticsEvent::SUPER_PROPERTIES`) sends the same five
 * keys with the same values, so client and server events land in one set of
 * partner and restaurant groups.
 *
 * IDs, enums and booleans only: nothing here may carry a name or an address.
 */

/** Mixpanel group keys. Their values are integers on every surface. */
export const AnalyticsGroupKey = {
  Partner: 'partner_id',
  Restaurant: 'restaurant_id',
};

/** `restaurant_source`; mirrors the backend's `RestaurantSource` enum. */
export const RestaurantSource = {
  FaDirect: 'fa_direct',
  GloriafoodMigration: 'gloriafood_migration',
  Partner: 'partner',
};

/** `service_providers.partner_type` values; mirrors the backend's `PartnerType` enum. */
export const PartnerType = {
  Reseller: 'reseller',
  FaHouse: 'fa_house',
};

/** `actor_role` when no user acted; the dashboard only sends it for a user without a role. */
export const SYSTEM_ACTOR_ROLE = 'system';

/** An id as the integer Mixpanel groups on, or null when there is none. */
export function toGroupId(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const id = Number(value);

  return Number.isInteger(id) ? id : null;
}

/**
 * The source a restaurant is counted under. Stored on the restaurant at creation
 * (`companies.restaurant_source`); without it — an older payload, or a partner-only
 * context with no restaurant — the source its restaurants get, by the backend's
 * `ResolveRestaurantSourceAction` rule: the master provider is FoodAmigos direct, a
 * FoodAmigos-house partner is a GloriaFood migration, any other partner is a partner.
 */
export function resolveRestaurantSource(company, serviceProvider) {
  if (company?.restaurant_source) {
    return company.restaurant_source;
  }

  if (!serviceProvider) {
    return null;
  }

  if (serviceProvider.is_master) {
    return RestaurantSource.FaDirect;
  }

  return serviceProvider.partner_type === PartnerType.FaHouse
    ? RestaurantSource.GloriafoodMigration
    : RestaurantSource.Partner;
}

/**
 * The super properties for the current tenant context. A key whose value is null is
 * not part of this context and must be cleared, not sent as null: `partner_id` for
 * FoodAmigos direct (the master provider), `restaurant_id` outside a restaurant.
 */
export function buildAnalyticsContext({ serviceProvider = null, company = null, primaryRole = null } = {}) {
  const partnerId = serviceProvider && !serviceProvider.is_master ? toGroupId(serviceProvider.id) : null;

  return {
    [AnalyticsGroupKey.Partner]: partnerId,
    [AnalyticsGroupKey.Restaurant]: toGroupId(company?.id),
    restaurant_source: resolveRestaurantSource(company, serviceProvider),
    actor_role: primaryRole ?? SYSTEM_ACTOR_ROLE,
    is_test: serviceProvider?.is_test === true,
  };
}

/**
 * Profile properties earlier builds set and the PRD forbids ("no personal data in any
 * profile property"). Unset on identify, so profiles created before the change are
 * cleaned the next time their user signs in.
 */
export const RETIRED_PROFILE_PROPERTIES = ['$email', '$name'];

/**
 * The Mixpanel user profile: when the account was created and the roles it holds.
 * Never the email or the name.
 */
export function buildProfileTraits(profile) {
  return {
    ...(profile?.created_at ? { $created: profile.created_at } : {}),
    roles: profile?.roles ?? [],
  };
}

const GROUP_KEYS = Object.values(AnalyticsGroupKey);

/**
 * Apply a context built by `buildAnalyticsContext`: register what it holds, join the
 * partner and restaurant groups, and clear every key it does not hold — so a company
 * switch replaces `restaurant_id` and leaving the restaurant drops it. `Analytics`
 * keeps all of it for this tab only (`TAB_CONTEXT_PROPERTIES`): another tab has its
 * own restaurant selected.
 */
export function applyAnalyticsContext(context, analytics = Analytics) {
  const properties = {};

  Object.entries(context).forEach(([key, value]) => {
    const isGroup = GROUP_KEYS.includes(key);

    if (value === null || value === undefined) {
      if (isGroup) {
        analytics.unsetGroup(key);
      } else {
        analytics.unregister(key);
      }

      return;
    }

    if (isGroup) {
      analytics.setGroup(key, value);
    } else {
      properties[key] = value;
    }
  });

  if (Object.keys(properties).length) {
    analytics.register(properties);
  }
}

/** Drop every key a tenant context registers — for a moment that has no session. */
export function clearAnalyticsContext(analytics = Analytics) {
  applyAnalyticsContext(Object.fromEntries(Object.keys(buildAnalyticsContext()).map((key) => [key, null])), analytics);
}
